/**
 * Thin transport over the PatientMobile_Api research endpoints.
 *
 * The API returns one of two shapes:
 *   - a serialised ADO.NET DataSet — { Table: [...], Table1: [...], ... }
 *   - a plain object — { Success, Message, ... }
 * Both are normalised here into { head, tables } so callers never have to care
 * which one they got. The first row of the first table is always the
 * Success / Message envelope the stored procedures agree on.
 */

const BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');

export interface ApiEnvelope {
  Success?: boolean;
  Message?: string;
  [key: string]: unknown;
}

export interface ApiResult<T extends ApiEnvelope = ApiEnvelope> {
  ok: boolean;
  message: string;
  head: T;
  tables: Record<string, unknown>[][];
}

export class ApiError extends Error {}

function tablesOf(payload: unknown): Record<string, unknown>[][] {
  if (!payload || typeof payload !== 'object') return [];
  const source = payload as Record<string, unknown>;

  /* A DataSet serialises its tables as Table, Table1, Table2 … in order. */
  const keys = Object.keys(source).filter((k) => /^Table\d*$/.test(k));
  if (keys.length === 0) return [];

  keys.sort((a, b) => {
    const na = a === 'Table' ? 0 : Number(a.slice(5));
    const nb = b === 'Table' ? 0 : Number(b.slice(5));
    return na - nb;
  });

  return keys.map((k) => (Array.isArray(source[k]) ? (source[k] as Record<string, unknown>[]) : []));
}

function normalise<T extends ApiEnvelope>(payload: unknown): ApiResult<T> {
  const tables = tablesOf(payload);
  const head = (tables.length > 0 && tables[0].length > 0
    ? tables[0][0]
    : (payload as Record<string, unknown>) ?? {}) as T;

  return {
    ok: head?.Success === true,
    message: typeof head?.Message === 'string' ? head.Message : '',
    head,
    tables
  };
}

async function parse(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    /* IIS error pages and the like come back as HTML. */
    return { Success: false, Message: response.ok ? 'Unexpected response from the server.' : text.slice(0, 300) };
  }
}

export async function post<T extends ApiEnvelope = ApiEnvelope>(
  path: string,
  body: Record<string, unknown>
): Promise<ApiResult<T>> {
  let response: Response;
  try {
    response = await fetch(`${BASE}/api/Research/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
  } catch {
    throw new ApiError('Could not reach the server. Check your connection and try again.');
  }

  const payload = await parse(response);
  const result = normalise<T>(payload);

  /* A 4xx with a readable message is a normal outcome, not an exception —
     it is the wording the screen should show. */
  if (!response.ok && !result.message) {
    throw new ApiError(`The server returned ${response.status}.`);
  }
  return result;
}

export async function get<T extends ApiEnvelope = ApiEnvelope>(
  path: string,
  query: Record<string, string | number> = {}
): Promise<ApiResult<T>> {
  const qs = new URLSearchParams(
    Object.entries(query).map(([k, v]) => [k, String(v)])
  ).toString();

  let response: Response;
  try {
    response = await fetch(`${BASE}/api/Research/${path}${qs ? `?${qs}` : ''}`);
  } catch {
    throw new ApiError('Could not reach the server. Check your connection and try again.');
  }

  return normalise<T>(await parse(response));
}

export async function upload<T extends ApiEnvelope = ApiEnvelope>(
  path: string,
  form: FormData
): Promise<ApiResult<T>> {
  let response: Response;
  try {
    response = await fetch(`${BASE}/api/Research/${path}`, { method: 'POST', body: form });
  } catch {
    throw new ApiError('Could not reach the server. Check your connection and try again.');
  }

  return normalise<T>(await parse(response));
}

/** URL for an uploaded document — used directly as an href. */
export function documentUrl(sessionToken: string, researchCode: number, docCode: number): string {
  const qs = new URLSearchParams({
    sessionToken,
    researchCode: String(researchCode),
    docCode: String(docCode)
  });
  return `${BASE}/api/Research/Document?${qs.toString()}`;
}

/**
 * URL for the merged submission document — the proposal typeset as a PDF with
 * every uploaded document appended, generated when the proposal is submitted.
 */
export function submissionPdfUrl(sessionToken: string, researchCode: number): string {
  const qs = new URLSearchParams({
    sessionToken,
    researchCode: String(researchCode)
  });
  return `${BASE}/api/Research/SubmissionPdf?${qs.toString()}`;
}

/** URL for the observation letter filed with the latest send-back. */
export function observationLetterUrl(sessionToken: string, researchCode: number): string {
  const qs = new URLSearchParams({
    sessionToken,
    researchCode: String(researchCode)
  });
  return `${BASE}/api/Research/ObservationLetter?${qs.toString()}`;
}

/** URL for the reviewer score report — IRB office only, enforced server-side. */
export function reviewReportUrl(sessionToken: string, researchCode: number): string {
  const qs = new URLSearchParams({
    sessionToken,
    researchCode: String(researchCode)
  });
  return `${BASE}/api/Research/Committee/ReviewReport?${qs.toString()}`;
}
